import _ from 'lodash'
import React from 'react'
import { queryFilters } from '../../Queries/queryFilters'
import CostCentreCollection from '../../State/Collections/CostCentreCollection'
import StaffCollection from '../../State/Collections/StaffCollection'
import ContactCollection from '../../State/Collections/ContactCollection'
import {
    canViewFlexiRemote,
    canViewPrimaryContact,
    canViewProjectExpenseBudgets,
    canViewProjectFees,
    canViewRevenueTargets,
    canViewStaffChargeOutRate,
    canViewStaffCostRate,
    canViewStaffPayRate,
} from '../../State/Permissions/HasPermissions'
import SessionStore from '../../State/SessionStore'

export const TimeReportColumns = (report) => ({
    project: {
        id: 'project',
        label: 'Project',
        type: 'project',
        width: 20,
        value: (row) => row.project,
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator]('projectId', value)
        },
    },
    projectPhase: {
        id: 'projectPhase',
        label: 'Phase',
        type: 'phase',
        width: 20,
        value: (row) => {
            return row.phase
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.text[operator]('phaseName', value)
        },
    },
    staffMember: {
        id: 'staffMember',
        label: 'Staff',
        type: 'staffMember',
        width: 20,
        value: (row) => {
            return row.staff
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator]('staffId', value)
        },
    },
    task: {
        id: 'task',
        label: 'Task',
        type: 'task',
        width: 20,
        value: (row) => {
            return row.task
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.text[operator]('taskName', value)
        },
    },
    date: {
        id: 'date',
        label: 'Date',
        type: 'date',
        width: 10,
        value: (row) => {
            return row.date
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.date[operator]('date', value)
        },
    },
    numMinutes: {
        id: 'numMinutes',
        label: 'Hours',
        type: 'number',
        width: 10,
        aggregate: 'sum',
        value: (row) => {
            return row.hours
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('numMinutes', value)
        },
    },
    notes: {
        id: 'notes',
        label: 'Notes',
        type: 'text',
        width: 30,
        value: (row) => {
            return row.notes
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.text[operator]('notes', value)
        },
    },
    costCentre: {
        id: 'costCentre',
        label: 'Cost Centre',
        type: 'costCentre',
        width: 15,
        value: (row) => {
            return CostCentreCollection.costCentresById[row.projectCostCentreId]
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator]('projectCostCentreId', value)
        },
    },
    staffCostCentre: {
        id: 'staffCostCentre',
        label: 'Staff Cost Centre',
        type: 'costCentre',
        width: 15,
        value: (row) => {
            return CostCentreCollection.costCentresById[row.staffCostCentreId]
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator]('staffCostCentreId', value)
        },
    },
    projectOwner: {
        id: 'projectOwner',
        label: 'Project Owner',
        type: 'staff',
        width: 20,
        value: (row) => {
            return StaffCollection.staffById[row.projectOwnerId]
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator]('projectOwnerId', value)
        },
    },
    projectContact: {
        id: 'projectContact',
        label: 'Project Client',
        type: 'contact',
        width: 20,
        value: (row) => {
            return ContactCollection.contactsById[row.projectContactId]
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator]('projectContactId', value)
        },
    },
    projectInvoiceContact: {
        id: 'projectInvoiceContact',
        label: 'Project Primary Contact',
        type: 'contact',
        width: 20,
        value: (row) => {
            return ContactCollection.contactsById[row.projectPrimaryContactId]
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator](
                'projectPrimaryContactId',
                value
            )
        },
        permissions: (row) => canViewPrimaryContact(),
    },
    projectCode: {
        id: 'projectCode',
        label: 'Project Code',
        type: 'text',
        width: 20,
        value: (row) => {
            return row.projectCode
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.text[operator]('projectCode', value)
        },
    },
    projectPhaseCode: {
        id: 'projectPhaseCode',
        label: 'Phase Code',
        type: 'text',
        width: 20,
        value: (row) => {
            return row.projectPhaseCode
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.text[operator]('projectPhaseCode', value)
        },
    },
    staffMemberFirstName: {
        id: 'staffMemberFirstName',
        label: 'Staff First Name',
        type: 'text',
        width: 20,
        value: (row) => {
            return row.staffMemberFirstName
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.text[operator]('staffMemberFirstName', value)
        },
    },
    staffMemberLastName: {
        id: 'staffMemberLastName',
        label: 'Staff Last Name',
        type: 'text',
        width: 20,
        value: (row) => {
            return row.staffMemberLastName
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.text[operator]('staffMemberLastName', value)
        },
    },
    staffRole: {
        id: 'staffRole',
        label: 'Staff Role',
        type: 'role',
        width: 20,
        value: (row) => {
            return row.staff?.role
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.singleId[operator]('staffRoleId', value)
        },
    },
    monthIndex: {
        id: 'monthIndex',
        label: 'Month',
        type: 'text',
        width: 20,
        value: (row) => {
            return row.month
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.text[operator]('month', value)
        },
    },
    isBillable: {
        id: 'isBillable',
        label: 'Billable',
        type: 'boolean',
        width: 20,
        value: (row) => {
            return row.isBillable
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.boolean[operator]('isBillable', value)
        },
    },
    isVariation: {
        id: 'isVariation',
        label: 'Variation',
        type: 'boolean',
        width: 20,
        value: (row) => {
            return row.isVariation
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.boolean[operator]('isVariation', value)
        },
    },
    isOvertime: {
        id: 'isOvertime',
        label: 'Overtime',
        type: 'boolean',
        width: 20,
        value: (row) => {
            return row.isOvertime
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.boolean[operator]('isOvertime', value)
        },
    },
    isLocked: {
        id: 'isLocked',
        label: 'Locked',
        type: 'boolean',
        width: 20,
        value: (row) => {
            return row.isLocked
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.boolean[operator]('isLocked', value)
        },
    },
    beenInvoiced: {
        id: 'beenInvoiced',
        label: 'Invoiced',
        type: 'boolean',
        width: 20,
        value: (row) => {
            return row.beenInvoiced
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.boolean[operator]('beenInvoiced', value)
        },
    },
    flexi: {
        id: 'flexi',
        label: 'Flexi',
        type: 'boolean',
        width: 20,
        value: (row) => {
            return row.flexi
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.boolean[operator]('flexi', value)
        },
        permissions: (row) => canViewFlexiRemote(),
    },
    remote: {
        id: 'remote',
        label: 'Remote',
        type: 'boolean',
        width: 20,
        value: (row) => {
            return row.remote
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.boolean[operator]('remote', value)
        },
        permissions: (row) => canViewFlexiRemote(),
    },
    fee: {
        id: 'fee',
        label: 'Fee',
        type: 'currency',
        width: 20,
        aggregate: 'sum',
        value: (row) => {
            return row.phase?.fee || row.project?.fee
        },
        permissions: (r) => canViewProjectFees(SessionStore.user, r?.project),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('projectFee', value)
        },
    },
    remainingFee: {
        id: 'remainingFee',
        label: 'Remaining Fee',
        type: 'currency',
        width: 20,
        aggregate: 'sum',
        value: (row) => {
            return row.phase?.fee
                ? row.phase?.fee - row.phase?.revenue
                : row.project?.fee -
                      _.sum(row.project?.phases.map((phase) => phase.revenue))
        },
        permissions: (r) =>
            canViewProjectFees(SessionStore.user, r?.project) &&
            canViewRevenueTargets(SessionStore.user, r?.project),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('projectRemainingFee', value)
        },
    },
    budget: {
        id: 'budget',
        label: 'Expense Budget',
        type: 'currency',
        width: 20,
        aggregate: 'sum',
        value: (row) => {
            return row.phase?.expenseBudget || row.project?.expenseBudget
        },
        permissions: (r) =>
            canViewProjectExpenseBudgets(SessionStore.user, r?.project),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('projectExpenseBudget', value)
        },
    },
    hoursBudget: {
        id: 'hoursBudget',
        label: 'Hours Budget',
        type: 'number',
        width: 20,
        aggregate: 'sum',
        value: (row) => {
            return row.phase?.hoursBudget || row.project?.hoursBudget
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('projectHoursBudget', value)
        },
    },
    startMinutes: {
        id: 'startMinutes',
        label: 'Start Time',
        type: 'time',
        width: 20,
        value: (row) => {
            return row.startMinutes
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.time[operator]('startMinutes', value)
        },
    },
    endMinutes: {
        id: 'endMinutes',
        label: 'End Time',
        type: 'time',
        width: 20,
        value: (row) => {
            return row.endMinutes
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.time[operator]('endMinutes', value)
        },
    },
    labourExpense: {
        id: 'labourExpense',
        label: 'Labour Expense',
        type: 'currency',
        width: 10,
        aggregate: 'sum',
        value: (row) => {
            return row.pay
        },
        permissions: (r) => canViewStaffPayRate(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('pay', value)
        },
    },
    cost: {
        id: 'cost',
        label: 'Expense (cost)',
        type: 'currency',
        width: 10,
        aggregate: 'sum',
        value: (row) => {
            return row.cost
        },
        permissions: (r) => canViewStaffCostRate(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('cost', value)
        },
    },
    chargeOut: {
        id: 'chargeOut',
        label: 'Charge Out',
        type: 'currency',
        width: 10,
        aggregate: 'sum',
        value: (row) => {
            return row.chargeOut
        },
        permissions: (r) => canViewStaffChargeOutRate(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('chargeOut', value)
        },
    },
    chargeOutRate: {
        id: 'chargeOutRate',
        label: 'Charge Out Rate',
        type: 'currency',
        width: 20,
        aggregate: 'sum',
        value: (row) => {
            return row.chargeOut / row.hours
        },
        permissions: (r) => canViewStaffChargeOutRate(SessionStore.user),
        queryFilters: ({ operator, value }) => {
            return queryFilters.number[operator]('chargeOutRate', value)
        },
    },
    hasNote: {
        id: 'hasNote',
        label: 'Has Note',
        type: 'boolean',
        width: 10,
        value: (row) => {
            return row.note && row.note.length > 0
        },
        queryFilters: ({ operator, value }) => {
            return queryFilters.boolean[operator]('hasNote', value)
        },
    },
})
